import * as React from "react";
import { LeadAnnotation } from "../misc/api/DeviceLinkApi/CalEcg";
import { maxBy } from "../misc/ArrayFunctions";
import { Dictionary } from 'lodash';
import groupBy from "lodash-es/groupBy";
import { roundAwayFromZero } from "../misc/math-functions";
import { CalculateHeartRate, CalculateQTcBazett, CalculateQTcFramingham, CalculateQTcFridericia, CalculateQTcHodges, DeriveParametersFromAnnotations, SortAnnotationsByLeadThenBeat } from "./EcgCalculations";
import { EcgTest } from "webportalclient";

export interface EcgTestAnnotationsProps {
    testAnnotations: LeadAnnotation[];
}

export interface EcgTestAnnotationsRhythmProps {
    maxBeats: number;
    groupedAnnotations: Dictionary<LeadAnnotation[]>;
}

export interface EcgTestAnnotationsRepresentativeBeatProps {
    groupedAnnotations: Dictionary<LeadAnnotation[]>;
}

const representativeBeatAnnotationCodeToName = (name: string) => {
    return name.replace("R_", "");
}

const leadCodeToName = (leadCode: number) => {
    switch (leadCode) {
        case 0:
            return "Global";
        case 1:
            return "I";
        case 2:
            return "II";
        case 3:
            return "III";
        case 4:
            return "aVR";
        case 5:
            return "aVL";
        case 6:
            return "aVF";
        case 7:
            return "V1";
        case 8:
            return "V2";
        case 9:
            return "V3";
        case 10:
            return "V4";
        case 11:
            return "V5";
        case 12:
            return "V6";
    }
}

const EcgTestAnnotationsRepresentativeBeat = ({ groupedAnnotations }: EcgTestAnnotationsRepresentativeBeatProps) => {
    return (
        <div className="col-md-6">
            <h3 className="text-primary">Representative Beat Annotations</h3>
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(groupedAnnotations).map(a => (
                        <tr key={a[1][0].annotation + a[1][0].leadIndex}>
                            <td>{representativeBeatAnnotationCodeToName(a[1][0].annotation)} ({leadCodeToName(a[1][0].leadIndex)}) (ms)</td>
                            {a[1].map(b =>
                            (<td key={b.annotation + b.leadIndex + b.beatIndex}>
                                {b.value ? roundAwayFromZero(b.value, 0) : "-"}
                            </td>))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const EcgTestAnnotationsRhythm = ({ maxBeats, groupedAnnotations }: EcgTestAnnotationsRhythmProps) => {
    return (
        <div className="col-md-12">
            <h3 className="text-primary">Rhythm Annotations</h3>
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th></th>
                        {[...Array(maxBeats).keys()].map(i => (
                            <th key={`Header_${i}`}>{i + 1}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(groupedAnnotations).map(a => (
                        <tr key={a[1][0].annotation + a[1][0].leadIndex}>
                            <td>{a[1][0].annotation} ({leadCodeToName(a[1][0].leadIndex)}) (ms)</td>
                            {a[1].map(b =>
                            (<td key={b.annotation + b.leadIndex + b.beatIndex}>
                                {b.value ? roundAwayFromZero(b.value, 0) : "-"}
                            </td>))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export interface EcgQTcTestParametersProps {
    rr: number;
    qt: number;
    siteTest?: EcgTest;
}

export const EcgQTcTestParameters = ({ rr, qt, siteTest }: EcgQTcTestParametersProps) => {
    const heartRate = rr ? CalculateHeartRate(rr) : null;
    const qtcBaz = qt & rr ? CalculateQTcBazett(qt, rr) : null;
    const qtcFri = qt & rr ? CalculateQTcFridericia(qt, rr) : null;
    const qtcFra = qt & rr ? CalculateQTcFramingham(qt, rr) : null;
    const qtcHod = qt & heartRate ? CalculateQTcHodges(qt, heartRate) : null;

    return (
        <div className="col-md-6">          
            <h3 className="text-primary">Parameters</h3>
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th></th>
                        {siteTest ? <th>Site</th> : null}
                        {siteTest ? <th>Overread</th> : <th></th>}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>HR (bpm)</td>
                        {siteTest ? <td>
                            {roundAwayFromZero(siteTest.hr)}
                        </td> : null}
                        <td>
                            {heartRate ? roundAwayFromZero(heartRate) : "-"}
                        </td>
                    </tr>
                    <tr>
                        <td>QTcB</td>
                        {siteTest ? <td>
                            {roundAwayFromZero(siteTest.qtcBaz)}
                        </td> : null}
                        <td>
                            {qtcBaz ? roundAwayFromZero(qtcBaz) : "-"}
                        </td>
                    </tr>
                    <tr>
                        <td>QTcF</td>
                        {siteTest ? <td>
                            {roundAwayFromZero(siteTest.qtcFri)}
                        </td> : null}
                        <td>
                            {qtcFri ? roundAwayFromZero(qtcFri) : "-"}
                        </td>
                    </tr>
                    <tr>
                        <td>QTcFra</td>
                        {siteTest ? <td>
                            {roundAwayFromZero(siteTest.qtcFra)}
                        </td> : null}
                        <td>
                            {qtcFra ? roundAwayFromZero(qtcFra) : "-"}
                        </td>
                    </tr>
                    <tr>
                        <td>QTcHod</td>
                        {siteTest ? <td>
                            {roundAwayFromZero(siteTest.qtcHod)}
                        </td> : null}
                        <td>
                            {qtcHod ? roundAwayFromZero(qtcHod) : "-"}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

export const EcgTestAnnotations = ({ testAnnotations }: EcgTestAnnotationsProps) => {
    if (testAnnotations === undefined || testAnnotations.length === 0)
        return null;

    const { rr, qt, groupedAnnotationsRhythm, groupedAnnotationsRepBeat } = DeriveParametersFromAnnotations(testAnnotations);
    const sortedAnnotations = testAnnotations.sort(SortAnnotationsByLeadThenBeat);

    const maxBeats = maxBy(sortedAnnotations, "beatIndex").beatIndex;
    return (<>
        <EcgTestAnnotationsRepresentativeBeat groupedAnnotations={groupedAnnotationsRepBeat} />
        <EcgQTcTestParameters qt={qt} rr={rr} />
        <EcgTestAnnotationsRhythm maxBeats={maxBeats} groupedAnnotations={groupedAnnotationsRhythm} />
    </>);
}
